import { useEffect, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { projectsService } from "../services/projects";
import { store } from "../redux/store";
import {
  LAST_SITE_HASH_KEY,
  setCurrentProject,
  setProjects,
  setLoaded,
  setLoadingProjects,
} from "../redux/project/projectSlice";

const LEGACY_SITE_PAGES = new Set([
  "preview",
  "data",
  "templates",
  "changes",
  "change-requests",
  "deployments",
  "deploys",
  "settings",
  "analytics",
  "seo",
]);

const siteSlugFromPath = (pathname) => {
  const raw = pathname.match(/^\/site\/([^/]+)\//)?.[1];
  return raw && !LEGACY_SITE_PAGES.has(raw) ? raw : null;
};

const matchProject = (projects, siteSlug) => (
  projects.find((project) => project.slug === siteSlug || project.hash === siteSlug) || null
);

const WorkspaceBootstrap = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { projects, loaded } = useSelector((state) => state.project);
  const siteSlug = siteSlugFromPath(location.pathname);
  const projectsRef = useRef(projects);
  projectsRef.current = projects;

  useEffect(() => {
    if (store.getState().project.loadingProjects) return;
    dispatch(setLoadingProjects(true));
    projectsService.listProjects(
      { page_size: 500 },
      (response) => {
        const results = Array.isArray(response.data)
          ? response.data
          : response.data?.results ?? [];
        dispatch(setProjects(results));
        dispatch(setLoaded(true));
        dispatch(setLoadingProjects(false));
      },
      () => {
        dispatch(setLoaded(true));
        dispatch(setLoadingProjects(false));
      }
    );
  }, [dispatch]);

  // Pick the open site once list is loaded. Don't redo on every list refresh, or
  // it replaces a full site response with the list row.
  // Order: URL slug/hash → last site in localStorage (if still in list) → first site.
  useLayoutEffect(() => {
    if (!loaded) return;
    const list = projectsRef.current;
    const current = store.getState().project.currentProject;

    if (siteSlug) {
      if (current && (current.slug === siteSlug || current.hash === siteSlug)) return;
      const match = matchProject(list, siteSlug);
      if (match) dispatch(setCurrentProject(match));
      return;
    }

    if (current && matchProject(list, current.hash)) return;

    const lastHash = localStorage.getItem(LAST_SITE_HASH_KEY);
    const fromStorage = lastHash ? matchProject(list, lastHash) : null;
    if (fromStorage) {
      dispatch(setCurrentProject(fromStorage));
      return;
    }

    if (list.length > 0) dispatch(setCurrentProject(list[0]));
  }, [dispatch, loaded, siteSlug]);

  return null;
};

export default WorkspaceBootstrap;
